import { letterFormLimits } from '../config/limits';
import type { NewLetter } from '../models/Letter';

export interface LetterFormValues {
  name: string;
  message: string;
}

/** Field name to message. An empty object means the form is valid. */
export type LetterFormErrors = Partial<Record<keyof LetterFormValues, string>>;

const { maxNameLength, maxMessageLength } = letterFormLimits;

/**
 * Validates the letter form.
 *
 * Separate from validateMemoryForm rather than sharing it with the nouns
 * swapped in: the limits differ, and so does what the messages should say.
 * Somebody writing a letter to their father is not "submitting a memory", and
 * a form that says so at the moment they get something wrong is a small
 * unkindness in a place that can afford to avoid one.
 *
 * As with the memory form, the Worker applies the same rules again. These are
 * for helpfulness; those are for safety.
 */
export function validateLetterForm(values: LetterFormValues): LetterFormErrors {
  const errors: LetterFormErrors = {};

  const name = values.name.trim();
  if (name === '') {
    errors.name = 'Please say who the letter is from.';
  } else if (name.length > maxNameLength) {
    errors.name = `Please keep the name to ${maxNameLength} characters or fewer.`;
  }

  const message = values.message.trim();
  if (message === '') {
    errors.message = 'Please write the letter before saving it.';
  } else if (message.length > maxMessageLength) {
    errors.message = `Please keep the letter to ${maxMessageLength.toLocaleString('en-GB')} characters or fewer.`;
  }

  return errors;
}

export function isValidLetterForm(errors: LetterFormErrors): boolean {
  return Object.keys(errors).length === 0;
}

/** Trims the form values into the shape the service expects. */
export function toNewLetter(values: LetterFormValues): NewLetter {
  return {
    name: values.name.trim(),
    message: values.message.trim(),
  };
}
